import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Loader2 } from "lucide-react";
import { format, parseISO, isWithinInterval } from "date-fns";
import { base44 } from "@/api/base44Client";

const SESSION_TYPES = [
  { value: "personal_training", label: "Personal Training" },
  { value: "group_class", label: "Group Class" },
  { value: "open_gym", label: "Open Gym" },
  { value: "assessment", label: "Assessment" },
];

export default function BookSessionModal({ open, onOpenChange, client, date, trainers, sessions, onSuccess }) {
  const [form, setForm] = useState({
    title: "Personal Training",
    time: "09:00",
    duration_minutes: 60,
    session_type: "personal_training",
    trainer: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Fetch all trainer sessions across all clients
  const { data: allTrainerSessions = [] } = useQuery({
    queryKey: ["allTrainerSessions", form.trainer],
    queryFn: () => base44.entities.TrainingSession.filter({}),
    enabled: !!form.trainer,
  });

  const selectedTrainer = trainers.find(t => t.id === form.trainer);
  const dateKey = date ? format(date, "yyyy-MM-dd") : null;
  
  // Get trainer's sessions for this day
  const trainerSessionsForDay = form.trainer && dateKey
    ? allTrainerSessions.filter(s => {
        if (s.status === "cancelled" || s.trainer !== form.trainer) return false;
        const sDate = s.date ? format(parseISO(s.date), "yyyy-MM-dd") : null;
        return sDate === dateKey;
      }).sort((a, b) => new Date(a.date) - new Date(b.date))
    : [];

  // Check if trainer has a conflict at this time
  const checkTrainerConflict = () => {
    if (!form.trainer || !date || !form.time) return false;
    
    const [hours, minutes] = form.time.split(":").map(Number);
    const sessionStart = new Date(date);
    sessionStart.setHours(hours, minutes, 0, 0);
    const sessionEnd = new Date(sessionStart.getTime() + form.duration_minutes * 60000);
    
    return allTrainerSessions.some(s => {
      if (s.status === "cancelled") return false;
      if (s.trainer !== form.trainer) return false;
      
      const existingStart = parseISO(s.date);
      const existingEnd = new Date(existingStart.getTime() + (s.duration_minutes || 60) * 60000);
      
      // Check if times overlap (start before other ends AND end after other starts)
      return sessionStart < existingEnd && sessionEnd > existingStart;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    
    if (checkTrainerConflict()) {
      setError("Trainer already has a session booked at this time.");
      return;
    }
    
    setSaving(true);
    try {
      const [hours, minutes] = form.time.split(":").map(Number);
      const sessionDate = new Date(date);
      sessionDate.setHours(hours, minutes, 0, 0);
      
      await base44.entities.TrainingSession.create({
        title: form.title,
        date: format(sessionDate, "yyyy-MM-dd'T'HH:mm"),
        duration_minutes: form.duration_minutes,
        session_type: form.session_type,
        trainer: form.trainer,
        notes: form.notes,
        status: "upcoming",
        sessionClientId: client.id,
      });
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      setError(err.message || "Failed to book session");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            Book Session — {date ? format(date, "EEEE, MMMM d") : ""}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSave} className="space-y-4">
          {error && (
            <div className="flex gap-2 items-start rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {/* Fixed date display + time input */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Date</Label>
              <div className="text-sm font-semibold mt-1 p-2 rounded-lg bg-muted/50">
                {date ? format(date, "EEE, MMM d") : "—"}
              </div>
            </div>
            <div>
              <Label>Time</Label>
              <Input 
                type="time" 
                value={form.time || "09:00"}
                onChange={e => set("time", e.target.value)}
                required 
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={e => set("title", e.target.value)} required />
            </div>
            <div>
              <Label>Duration</Label>
              <Select value={String(form.duration_minutes)} onValueChange={v => set("duration_minutes", parseInt(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[30, 60, 90, 120].map(m => (
                    <SelectItem key={m} value={String(m)}>{m < 60 ? `${m} min` : `${m / 60} hr`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.session_type} onValueChange={v => set("session_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SESSION_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Trainer</Label>
              <Select value={form.trainer || ""} onValueChange={v => set("trainer", v)}>
                <SelectTrigger><SelectValue placeholder="Select trainer" /></SelectTrigger>
                <SelectContent>
                  {trainers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Trainer schedule for this day */}
          {form.trainer && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2">
              <p className="text-xs font-semibold text-primary">{selectedTrainer?.name}'s Schedule for This Day</p>
              {trainerSessionsForDay.length === 0 ? (
                <p className="text-xs text-muted-foreground">No other sessions scheduled</p>
              ) : (
                <div className="space-y-1.5">
                  {trainerSessionsForDay.map(session => (
                    <div key={session.id} className="flex items-center justify-between bg-white rounded-md p-2 text-xs">
                      <div>
                        <p className="font-medium text-foreground">{session.title}</p>
                        <p className="text-muted-foreground">
                          {session.date ? format(parseISO(session.date), "h:mm a") : "—"}
                          {session.duration_minutes ? ` (${session.duration_minutes} min)` : ""}
                        </p>
                      </div>
                      <Badge variant="secondary" className="text-xs shrink-0">{session.session_type}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} />
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={saving || checkTrainerConflict()}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Book Session"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}